/**
 * Tela /tvs/:tvId (TH-53164 / M1.E4.S4.2 / PSHWO-57471).
 *
 * Tabs:
 * - Geral: edição inline name/area_id + campos de operação editáveis
 *   (physical_location, installed_at, homologated_model, responsible, notes).
 * - Telemetria: campos reportados pelo player (device, rede, saúde) — read-only.
 * - Playlist: editor de playlist (existente).
 * - Comandos: placeholder (M4).
 *
 * `Rotacionar token` exige role tenant_admin (ACL backend TH-53146).
 * Para super_admin → 403. UI mostra tooltip dizendo o motivo.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { PlaylistEditorTab } from "@/components/playlist/PlaylistEditorTab";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";
import { ActivationQRDialog } from "@/pages/store-detail/ActivationQRDialog";
import { ConfirmDialog } from "@/pages/tv-detail/ConfirmDialog";
import { cn } from "@/lib/utils";

/** Campos completos de TVRead — PSHWO-57471 + PSHWO-57470. */
interface TV {
  id: string;
  store_id: string;
  name: string;
  area_id: string | null;
  // Campos de operação (editáveis)
  physical_location: string | null;
  installed_at: string | null;
  homologated_model: string | null;
  responsible: string | null;
  notes: string | null;
  // Telemetria de hardware (read-only — vinda do player)
  device_model: string | null;
  device_brand: string | null;
  mac_address: string | null;
  android_sdk: number | null;
  os_version: string | null;
  app_version: string | null;
  app_version_code: number | null;
  screen_width: number | null;
  screen_height: number | null;
  refresh_rate: number | null;
  orientation: string | null;
  connection_type: string | null;
  wifi_ssid: string | null;
  wifi_rssi: number | null;
  local_ip: string | null;
  storage_free_bytes: number | null;
  ram_total_mb: number | null;
  ram_available_mb: number | null;
  uptime_seconds: number | null;
  last_sync_at: string | null;
  last_seen_at: string | null;
}

interface Area {
  id: string;
  name: string;
}

type Tab = "geral" | "telemetria" | "playlist" | "comandos";

async function fetchTV(id: string): Promise<TV> {
  const res = await fetch(`/api/tvs/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TV;
}

async function fetchAreas(): Promise<Area[]> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch(`/api/areas?per_page=100`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as { data: Area[] };
  return body.data;
}

async function patchTV(
  id: string,
  payload: {
    name?: string;
    area_id?: string | null;
    physical_location?: string | null;
    installed_at?: string | null;
    homologated_model?: string | null;
    responsible?: string | null;
    notes?: string | null;
  },
): Promise<void> {
  const res = await fetch(`/api/tvs/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

async function rotateToken(id: string): Promise<void> {
  const res = await fetch(`/api/tvs/${id}/rotate-token`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

async function removeTV(id: string): Promise<void> {
  const res = await fetch(`/api/tvs/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

/** Formata bytes em string legível (KB / MB / GB). */
function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Formata segundos em horas/minutos. */
function formatUptime(seconds: number | null): string {
  if (seconds === null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Formata data ISO para exibição local. */
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Converte datetime-local string para ISO (com Z). */
function datetimeLocalToISO(value: string): string | null {
  if (!value.trim()) return null;
  return new Date(value).toISOString();
}

/** Converte ISO para formato datetime-local (YYYY-MM-DDTHH:mm). */
function isoToDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TVDetailPage() {
  const { tvId } = useParams({ from: "/tvs/$tvId" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>("geral");
  const [showQR, setShowQR] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const tvQ = useQuery({
    queryKey: ["tv", tvId],
    queryFn: () => fetchTV(tvId),
    staleTime: 30_000,
    enabled: !!user,
  });

  const areasQ = useQuery({
    queryKey: ["areas", "all"],
    queryFn: fetchAreas,
    staleTime: 5 * 60_000,
    enabled: !!user,
  });

  const isTenantAdmin = user?.role === "tenant_admin";
  const canManage = isTenantAdmin || user?.role === "super_admin";

  const title = tvQ.data?.name ?? (tvQ.isLoading ? "Carregando…" : "Tela");
  const breadcrumb = tvQ.data
    ? `OPERAÇÃO / TELAS / ${tvQ.data.name.toUpperCase()}`
    : "OPERAÇÃO / TELAS";

  return (
    <RequireAuth>
      <AppLayout breadcrumb={breadcrumb} title={title}>
        <div
          className={cn(
            "mx-auto",
            tab === "playlist" ? "max-w-7xl" : "max-w-4xl",
          )}
        >
          {tvQ.isLoading ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              carregando…
            </p>
          ) : tvQ.error ? (
            <p className="text-sm text-[var(--color-destructive)]">
              erro ao carregar TV
            </p>
          ) : tvQ.data ? (
            <>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                ID: <code>{tvQ.data.id}</code>
              </p>

              <nav
                role="tablist"
                aria-label="Abas da TV"
                className="mt-6 border-b border-[var(--color-border)]"
              >
                {/* "Comandos" fora das abas até ter UI (era placeholder; a API já aceita
                    os comandos) — PSHWO-67349. */}
                {(["geral", "telemetria", "playlist"] as const).map(
                  (t) => (
                    <TabButton
                      key={t}
                      active={tab === t}
                      onClick={() => setTab(t)}
                      label={
                        t === "geral"
                          ? "Geral"
                          : t === "telemetria"
                            ? "Telemetria"
                            : t === "playlist"
                              ? "Playlist"
                              : "Comandos"
                      }
                    />
                  ),
                )}
              </nav>

              <div className="mt-6">
                {tab === "geral" ? (
                  <GeneralTab
                    tv={tvQ.data}
                    areas={areasQ.data ?? []}
                    canManage={canManage}
                    isTenantAdmin={isTenantAdmin}
                    onSavedField={() =>
                      queryClient.invalidateQueries({
                        queryKey: ["tv", tvId],
                      })
                    }
                    onShowQR={() => setShowQR(true)}
                    onRotate={() => setConfirmRotate(true)}
                    onRemove={() => setConfirmRemove(true)}
                  />
                ) : tab === "telemetria" ? (
                  <TelemetriaTab tv={tvQ.data} />
                ) : tab === "playlist" ? (
                  <PlaylistEditorTab
                    tvId={tvId}
                    tvName={tvQ.data.name}
                    tvHasArea={tvQ.data.area_id !== null}
                    canManage={canManage}
                  />
                ) : (
                  <PlaceholderTab label="Comandos" />
                )}
              </div>
            </>
          ) : null}
        </div>

        <ActivationQRDialog
          open={showQR}
          onOpenChange={setShowQR}
          tvId={tvId}
          tvName={tvQ.data?.name}
        />

        <ConfirmDialog
          open={confirmRotate}
          onOpenChange={setConfirmRotate}
          title="Rotacionar token?"
          description="O token atual deixa de funcionar imediatamente. Qualquer player rodando para de exibir até ser ativado de novo via QR. Útil quando suspeita-se que o token vazou."
          confirmLabel="Rotacionar"
          destructive
          onConfirm={() => rotateToken(tvId)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["tv", tvId] });
            setShowQR(true); // já abre o QR novo pra reativação
          }}
        />

        <ConfirmDialog
          open={confirmRemove}
          onOpenChange={setConfirmRemove}
          title="Remover TV?"
          description={`A TV "${tvQ.data?.name ?? ""}" será apagada permanentemente. Não é reversível.`}
          confirmLabel="Remover"
          destructive
          onConfirm={() => removeTV(tvId)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["tvs"] });
            if (tvQ.data) {
              navigate({
                to: "/stores/$storeId",
                params: { storeId: tvQ.data.store_id },
                replace: true,
              });
            } else {
              navigate({ to: "/stores", replace: true });
            }
          }}
        />
      </AppLayout>
    </RequireAuth>
  );
}

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
        active
          ? "border-[var(--color-primary)] text-[var(--color-foreground)]"
          : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
      )}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Aba Geral — identificação + operação + ações
// ─────────────────────────────────────────────────────────────────────────────

interface GeneralTabProps {
  tv: TV;
  areas: Area[];
  canManage: boolean;
  isTenantAdmin: boolean;
  onSavedField: () => void;
  onShowQR: () => void;
  onRotate: () => void;
  onRemove: () => void;
}

function GeneralTab({
  tv,
  areas,
  canManage,
  isTenantAdmin,
  onSavedField,
  onShowQR,
  onRotate,
  onRemove,
}: GeneralTabProps) {
  const [name, setName] = useState(tv.name);
  const [areaId, setAreaId] = useState(tv.area_id ?? "");
  // Campos de operação
  const [physicalLocation, setPhysicalLocation] = useState(tv.physical_location ?? "");
  const [installedAt, setInstalledAt] = useState(isoToDatetimeLocal(tv.installed_at));
  const [homologatedModel, setHomologatedModel] = useState(tv.homologated_model ?? "");
  const [responsible, setResponsible] = useState(tv.responsible ?? "");
  const [notes, setNotes] = useState(tv.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const saveName = useMutation({
    mutationFn: () => patchTV(tv.id, { name }),
    onSuccess: () => { setError(null); onSavedField(); },
    onError: (err: Error) => setError(err.message),
  });

  const saveArea = useMutation({
    mutationFn: () => patchTV(tv.id, { area_id: areaId === "" ? null : areaId }),
    onSuccess: () => { setError(null); onSavedField(); },
    onError: (err: Error) => setError(err.message),
  });

  const saveOperation = useMutation({
    mutationFn: () =>
      patchTV(tv.id, {
        physical_location: physicalLocation.trim() || null,
        installed_at: datetimeLocalToISO(installedAt),
        homologated_model: homologatedModel.trim() || null,
        responsible: responsible.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => { setError(null); onSavedField(); },
    onError: (err: Error) => setError(err.message),
  });

  const operationChanged =
    physicalLocation !== (tv.physical_location ?? "") ||
    installedAt !== isoToDatetimeLocal(tv.installed_at) ||
    homologatedModel !== (tv.homologated_model ?? "") ||
    responsible !== (tv.responsible ?? "") ||
    notes !== (tv.notes ?? "");

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
      <div className="flex flex-col gap-6">
        {/* ── Identificação ── */}
        <Card>
          <h2 className="text-base font-semibold">Identificação</h2>
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="tv-detail-name">Nome</Label>
              <div className="flex gap-2">
                <Input
                  id="tv-detail-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canManage}
                />
                <Button
                  variant="outline"
                  disabled={!canManage || saveName.isPending || name === tv.name || !name.trim()}
                  onClick={() => saveName.mutate()}
                >
                  {saveName.isPending ? "Salvando…" : "Salvar"}
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="tv-detail-area">Área</Label>
              <div className="flex gap-2">
                <Select
                  id="tv-detail-area"
                  value={areaId}
                  onChange={(e) => setAreaId(e.target.value)}
                  disabled={!canManage}
                >
                  <option value="">— sem área —</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
                <Button
                  variant="outline"
                  disabled={!canManage || saveArea.isPending || areaId === (tv.area_id ?? "")}
                  onClick={() => saveArea.mutate()}
                >
                  {saveArea.isPending ? "Salvando…" : "Salvar"}
                </Button>
              </div>
            </div>

            {error ? (
              <div
                role="alert"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                {error}
              </div>
            ) : null}
          </div>
        </Card>

        {/* ── Operação ── */}
        <Card>
          <h2 className="text-base font-semibold">Operação</h2>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Informações preenchidas pelo time de campo ou administrador da loja.
          </p>
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="tv-physical-location">Localização física</Label>
              <Input
                id="tv-physical-location"
                value={physicalLocation}
                onChange={(e) => setPhysicalLocation(e.target.value)}
                placeholder="Ex.: Seção Hortifruti — Parede Leste"
                disabled={!canManage}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="tv-installed-at">Data de instalação</Label>
              <Input
                id="tv-installed-at"
                type="datetime-local"
                value={installedAt}
                onChange={(e) => setInstalledAt(e.target.value)}
                disabled={!canManage}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="tv-homologated-model">Modelo homologado</Label>
              <Input
                id="tv-homologated-model"
                value={homologatedModel}
                onChange={(e) => setHomologatedModel(e.target.value)}
                placeholder="Ex.: H96 Max V12"
                disabled={!canManage}
              />
              <span className="text-xs text-[var(--color-muted-foreground)]">
                Conforme lista em hardware-homologated.md
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="tv-responsible">Responsável</Label>
              <Input
                id="tv-responsible"
                value={responsible}
                onChange={(e) => setResponsible(e.target.value)}
                placeholder="Nome do técnico ou gestor da loja"
                disabled={!canManage}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="tv-notes">Observações</Label>
              <textarea
                id="tv-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observações livres de operação"
                disabled={!canManage}
                className="flex w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)] disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              />
            </div>

            {saveOperation.isError ? (
              <div
                role="alert"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                {(saveOperation.error as Error).message}
              </div>
            ) : null}

            <div className="flex justify-end">
              <Button
                variant="outline"
                disabled={!canManage || saveOperation.isPending || !operationChanged}
                onClick={() => saveOperation.mutate()}
              >
                {saveOperation.isPending ? "Salvando…" : "Salvar operação"}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Ações ── */}
      <Card>
        <h2 className="text-base font-semibold">Ações</h2>
        <div className="mt-4 flex flex-col gap-3">
          <Button onClick={onShowQR} disabled={!canManage}>
            Gerar QR
          </Button>
          <Button
            variant="outline"
            className="text-[var(--color-destructive)]"
            onClick={onRotate}
            disabled={!isTenantAdmin}
            title={!isTenantAdmin ? "Apenas tenant_admin pode rotacionar token" : undefined}
          >
            Rotacionar token
          </Button>
          <Button
            variant="destructive"
            onClick={onRemove}
            disabled={!canManage}
          >
            Remover TV
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Aba Telemetria — dados read-only reportados pelo player
// ─────────────────────────────────────────────────────────────────────────────

function TelemetriaTab({ tv }: { tv: TV }) {
  return (
    <div className="flex flex-col gap-6">
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Campos reportados automaticamente pelo player. Somente leitura.
      </p>

      {/* ── Identificação do dispositivo ── */}
      <Card>
        <h2 className="text-base font-semibold">Identificação</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TelemetryField label="Modelo do dispositivo" value={tv.device_model} />
          <TelemetryField label="Fabricante" value={tv.device_brand} />
          <TelemetryField label="MAC address" value={tv.mac_address} mono />
          <TelemetryField label="Android SDK" value={tv.android_sdk?.toString() ?? null} />
          <TelemetryField label="Versão do SO" value={tv.os_version} />
          <TelemetryField label="Versão do app" value={tv.app_version} />
          <TelemetryField label="Código de versão" value={tv.app_version_code?.toString() ?? null} />
        </div>
      </Card>

      {/* ── Display ── */}
      <Card>
        <h2 className="text-base font-semibold">Display</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TelemetryField
            label="Resolução"
            value={
              tv.screen_width && tv.screen_height
                ? `${tv.screen_width} × ${tv.screen_height} px`
                : null
            }
          />
          <TelemetryField
            label="Taxa de atualização"
            value={tv.refresh_rate ? `${tv.refresh_rate} Hz` : null}
          />
          <TelemetryField label="Orientação" value={tv.orientation} />
        </div>
      </Card>

      {/* ── Rede ── */}
      <Card>
        <h2 className="text-base font-semibold">Rede</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TelemetryField label="Tipo de conexão" value={tv.connection_type} />
          <TelemetryField label="Rede Wi-Fi" value={tv.wifi_ssid} />
          <TelemetryField
            label="Sinal Wi-Fi (RSSI)"
            value={tv.wifi_rssi !== null ? `${tv.wifi_rssi} dBm` : null}
          />
          <TelemetryField label="IP local" value={tv.local_ip} mono />
        </div>
      </Card>

      {/* ── Saúde ── */}
      <Card>
        <h2 className="text-base font-semibold">Saúde</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TelemetryField
            label="Armazenamento livre"
            value={formatBytes(tv.storage_free_bytes)}
          />
          <TelemetryField
            label="RAM total"
            value={tv.ram_total_mb !== null ? `${tv.ram_total_mb} MB` : null}
          />
          <TelemetryField
            label="RAM disponível"
            value={tv.ram_available_mb !== null ? `${tv.ram_available_mb} MB` : null}
          />
          <TelemetryField label="Uptime" value={formatUptime(tv.uptime_seconds)} />
          <TelemetryField label="Última sincronização" value={formatDate(tv.last_sync_at)} />
          <TelemetryField label="Último contato" value={formatDate(tv.last_seen_at)} />
        </div>
      </Card>
    </div>
  );
}

/** Campo somente-leitura da aba de Telemetria. */
function TelemetryField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <span
        className={cn(
          "text-sm text-[var(--color-foreground)]",
          mono && "font-mono tabular-nums",
          (!value || value === "—") && "text-[var(--color-muted-foreground)] italic",
        )}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Placeholder tab (Comandos)
// ─────────────────────────────────────────────────────────────────────────────

function PlaceholderTab({ label }: { label: string }) {
  return (
    <Card>
      <h2 className="text-base font-semibold">{label}</h2>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        Implementada em M4 (player Android Lock Task + telemetria).
      </p>
    </Card>
  );
}
